import plur from 'plur';

/**
 * Delay durations as the delay cards phrase them.
 *
 * Mirrors Ember's `timeframe-unit` helper and `round-timeframe-units`: a
 * duration is shown in the LARGEST unit that divides it evenly, so 93600
 * seconds reads `26 hours`, not `1 day, 2 hours`. That is deliberately not
 * `formatDelayDuration` (engine/delay-path-description), which spells out every
 * component and is what wait-until delay paths use. The two are not
 * interchangeable, and each card uses the one Ember uses.
 */

export type TimeframeUnitName = 'minute' | 'hour' | 'day' | 'week';

export type TimeframeUnit = {
  value: TimeframeUnitName;
  seconds: number;
};

const MINUTE: TimeframeUnit = { value: 'minute', seconds: 60 };
const HOUR: TimeframeUnit = { value: 'hour', seconds: 60 * 60 };
const DAY: TimeframeUnit = { value: 'day', seconds: 24 * 60 * 60 };
const WEEK: TimeframeUnit = { value: 'week', seconds: 7 * 24 * 60 * 60 };

/** Smallest to largest, the order `findRoundUnit` scans. */
export const TIMEFRAME_UNITS: readonly TimeframeUnit[] = [MINUTE, HOUR, DAY, WEEK];

/** The smallest delay granularity is a minute, so stray seconds are dropped. */
export function roundTimeframe(seconds: number): number {
  return Math.floor(seconds / 60) * 60;
}

/**
 * The largest unit that divides `timeframe` evenly; the smallest allowed unit
 * when none does.
 *
 * `allowed` narrows the units a duration may be phrased in, for a control that
 * offers only some of them: a re-entry interval a user can set in hours, days
 * or weeks never reads in minutes, even when only the minute divides it.
 */
export function findRoundUnit(timeframe: number, allowed?: readonly TimeframeUnitName[]): TimeframeUnit {
  const units = allowed == null ? TIMEFRAME_UNITS : TIMEFRAME_UNITS.filter((unit) => allowed.includes(unit.value));
  let found = units[0] ?? MINUTE;
  for (const unit of units) {
    if (timeframe % unit.seconds === 0) {
      found = unit;
    }
  }
  return found;
}

export type RoundedTimeframe = {
  /** Whole units, rounded up. */
  value: number;
  unit: TimeframeUnit;
};

/**
 * Rounds a duration to its display unit.
 *
 * Zero divides every unit, so a zero duration lands on the largest one (a
 * week) unless `defaultUnitWhenZero` names the unit the caller wants an
 * unset delay to read in.
 */
export function roundTimeframeAndUnit(
  seconds: number,
  defaultUnitWhenZero: TimeframeUnitName | null,
): RoundedTimeframe {
  const timeframe = roundTimeframe(seconds);
  let unit = findRoundUnit(timeframe);

  if (timeframe === 0 && defaultUnitWhenZero != null) {
    unit = TIMEFRAME_UNITS.find((candidate) => candidate.value === defaultUnitWhenZero) ?? unit;
  }

  return { value: Math.ceil(timeframe / unit.seconds), unit };
}

export type TimeframeUnitOptions = {
  defaultUnitWhenZero?: TimeframeUnitName | null;
  /** `false` yields the bare count, for a range whose two ends share a unit. */
  shouldShowUnit?: boolean;
};

/** Ember's `timeframeUnit` helper: `26 hours`, `1 day`, `0 days`. */
export function timeframeUnit(seconds: number, options: TimeframeUnitOptions = {}): string {
  const { value, unit } = roundTimeframeAndUnit(seconds, options.defaultUnitWhenZero ?? null);

  if (options.shouldShowUnit === false) {
    return `${value}`;
  }

  return `${value} ${plur(unit.value, value)}`;
}

/**
 * Ember's `formatDuration` in `wait-until-path-description`: the wait-until
 * maximum-wait and event-offset prose. Unlike `timeframeUnit`, it never rounds
 * up and has no zero default, so a zero reads `0 weeks`.
 */
export function formatRoundDuration(seconds: number): string {
  const timeframe = roundTimeframe(seconds);
  const unit = findRoundUnit(timeframe);
  const count = timeframe / unit.seconds;
  return `${count} ${plur(unit.value, count)}`;
}
